import {
  ApiFilterOperation,
  HookedSource,
  Source,
  abortableFetch,
  listItemsFactory,
  useScrollableSource,
} from '@eas/common-web';
import { useHistorySource } from '@composite/history/history-api';
import { ChallengedAct, CourtFile, DocumentAttachment, EsFile } from '@models';
import {
  Agenda,
  DeadlineState,
  EvidenceApiUrl,
  HistoryEntityType,
  ParticipantType,
  RegulationType,
  TaskState,
} from '@enums';

export const NOTE_SOURCE = 'NOTE_SOURCE';
export const DEADLINE_SOURCE = 'DEADLINE_SOURCE';
export const RUNNING_DEADLINE_SOURCE = 'RUNNING_DEADLINE_SOURCE';
export const DOCUMENT_SOURCE = 'DOCUMENT_SOURCE';
export const COVER_LETTER_SOURCE = 'COVER_LETTER_SOURCE';
export const DECISION_SOURCE = 'DECISION_SOURCE';
export const PROPOSER_SOURCE = 'PROPOSER_SOURCE';
export const ACCESSORY_PROPOSER_SOURCE = 'ACCESSORY_PROPOSER_SOURCE';
export const LEGAL_ADVISER_SOURCE = 'LEGAL_ADVISER_SOURCE';
export const WRITER_SOURCE = 'WRITER_SOURCE_SOURCE';
export const CHALLENGED_ACT_SOURCE = 'CHALLENGED_ACT_SOURCE';
export const REGULATION_SOURCE = 'REGULATION_SOURCE';
export const REGULATION_CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES_SOURCE =
  'REGULATION_CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES_SOURCE';
export const REGULATION_EUROPEAN_UNION_LAW_SOURCE =
  'REGULATION_EUROPEAN_UNION_LAW_SOURCE';
export const REGULATION_OTHER_SOURCE = 'REGULATION_OTHER_SOURCE';
export const TASK_SOURCE = 'TASK_SOURCE';
export const DRAFT_SOURCE = 'DRAFT_SOURCE';
export const VIEWING_SOURCE = 'VIEWING_SOURCE';
export const VIEWING_REQUEST_SOURCE = 'VIEWING_REQUEST_SOURCE';
export const LOAN_SOURCE = 'LOAN_SOURCE';
export const SAME_PROPOSER_FILE_SOURCE = 'SAME_PROPOSER_FILE_SOURCE';
export const SAME_CHALLENGED_ACT_FILE_SOURCE =
  'SAME_CHALLENGED_ACT_FILE_SOURCE';
export const SAME_LEGAL_ADVISER_FILE_SOURCE = 'SAME_LEGAL_ADVISER_FILE_SOURCE';

type CoverLetterResponse =
  | DocumentAttachment[]
  | Pick<
      Source<DocumentAttachment>,
      'aggregations' | 'highlights' | 'searchAfter' | 'items' | 'count'
    >
  | undefined;

function toCoverLetterSource(
  data: CoverLetterResponse
): Source<DocumentAttachment> {
  const result = Array.isArray(data)
    ? { items: data, count: data.length }
    : data ?? { items: [], count: 0 };

  return {
    ...result,
    loading: false,
    setLoading: () => undefined,
    reset: () => undefined,
  };
}

function useNoteSource(): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [NOTE_SOURCE]: {
      source,
      shouldLoad: () => true,
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.ES_FILES}/${id}/note/list`);
      },
    },
  };
}

function useDeadlineSource(): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [DEADLINE_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => {
        return [
          Agenda.COURT,
          Agenda.SPR,
          Agenda.INF,
          Agenda.DISCIPLINARY_PROCEEDINGS,
        ].includes(agenda!);
      },
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.DEADLINES}/list`);
        source.setParams({
          size: 10,
          sort: [
            {
              field: 'endDate',
              type: 'FIELD',
              order: 'ASC',
            },
          ],
          filters: [
            {
              field: 'document.esFile.id',
              operation: ApiFilterOperation.EQ,
              value: id,
            },
          ],
        });
      },
    },
  };
}

function useRunningDeadlineSource(): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [RUNNING_DEADLINE_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => {
        return [
          Agenda.COURT,
          Agenda.SPR,
          Agenda.INF,
          Agenda.DISCIPLINARY_PROCEEDINGS,
        ].includes(agenda!);
      },
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.DEADLINES}/list`);
        source.setParams({
          size: 10,
          filters: [
            {
              field: 'state.id',
              operation: ApiFilterOperation.EQ,
              value: DeadlineState.RUNNING,
            },
            {
              field: 'document.esFile.id',
              operation: ApiFilterOperation.EQ,
              value: id,
            },
          ],
          sort: [
            {
              field: 'endDate',
              type: 'FIELD',
              order: 'ASC',
            },
          ],
        });
      },
    },
  };
}

function useDocumentSource(): HookedSource<EsFile> {
  const source = useScrollableSource({
    url: `${EvidenceApiUrl.DOCUMENTS}/list`,
  });

  return {
    [DOCUMENT_SOURCE]: {
      source,
      shouldLoad: () => true,
      modifyBeforeLoad: ({ id }) => {
        source.setParams({
          size: 50,
          filters: [
            {
              field: 'esFile.id',
              operation: ApiFilterOperation.EQ,
              value: id,
            },
          ],
          sort: [
            {
              field: 'order',
              type: 'FIELD',
              order: 'DESC',
            },
          ],
        });
      },
    },
  };
}

function useCoverLetterSource(): HookedSource<EsFile> {
  const source = useScrollableSource<DocumentAttachment>({
    listItems: listItemsFactory<DocumentAttachment>({
      listItemsMethod: (url) =>
        abortableFetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }),
      postProcess: async (data: CoverLetterResponse) =>
        toCoverLetterSource(data),
    }),
  });

  return {
    [COVER_LETTER_SOURCE]: {
      source,
      shouldLoad: () => true,
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.ES_FILES}/${id}/cover-letter`);
      },
    },
  };
}

function useDecisionSource(): HookedSource<EsFile> {
  const source = useScrollableSource({
    url: `${EvidenceApiUrl.DECISIONS}/list`,
  });

  return {
    [DECISION_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) =>
        [Agenda.COURT, Agenda.PLENARY_OPINION].includes(agenda!),
      modifyBeforeLoad: ({ id }) => {
        source.setParams({
          size: 10,
          filters: [
            {
              field: 'esFile.id',
              operation: ApiFilterOperation.EQ,
              value: id,
            },
          ],
          sort: [
            {
              type: 'FIELD',
              field: 'number',
              order: 'ASC',
            },
          ],
        });
      },
    },
  };
}

function useProposerSource(): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [PROPOSER_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => {
        return [Agenda.COURT].includes(agenda!);
      },
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.ES_FILES}/${id}/participant/list`);
        source.setParams({
          size: 15,
          sort: [
            {
              field: 'created',
              order: 'ASC',
              type: 'FIELD',
            },
          ],
          filters: [
            {
              field: 'type.id',
              operation: ApiFilterOperation.EQ,
              value: ParticipantType.PROPOSER,
            },
          ],
        });
      },
    },
  };
}

function useAccessoryProposerSource(): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [ACCESSORY_PROPOSER_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => {
        return [Agenda.COURT].includes(agenda!);
      },
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.ES_FILES}/${id}/participant/list`);
        source.setParams({
          size: 15,
          sort: [
            {
              field: 'contact.subject.name',
              order: 'ASC',
              type: 'FIELD',
            },
          ],
          filters: [
            {
              field: 'type.id',
              operation: ApiFilterOperation.EQ,
              value: ParticipantType.ACCESSORY_PARTICIPANT,
            },
          ],
        });
      },
    },
  };
}

function useLegalAdviserSource(): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [LEGAL_ADVISER_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => {
        return [Agenda.COURT].includes(agenda!);
      },
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.ES_FILES}/${id}/participant/list`);
        source.setParams({
          size: 15,
          sort: [
            {
              field: 'contact.subject.name',
              order: 'ASC',
              type: 'FIELD',
            },
          ],
          filters: [
            {
              field: 'type.id',
              operation: ApiFilterOperation.EQ,
              value: ParticipantType.LEGAL_ADVISER,
            },
          ],
        });
      },
    },
  };
}

function useWriterSource(): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [WRITER_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => {
        return [Agenda.INF, Agenda.SPR].includes(agenda!);
      },
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.ES_FILES}/${id}/participant/list`);
        source.setParams({
          size: 15,
          sort: [
            {
              field: 'contact.subject.name',
              order: 'ASC',
              type: 'FIELD',
            },
          ],
          filters: [
            {
              field: 'type.id',
              operation: ApiFilterOperation.EQ,
              value: ParticipantType.WRITER,
            },
          ],
        });
      },
    },
  };
}

function useRegulationSource(): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [REGULATION_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => {
        return [Agenda.COURT, Agenda.PLENARY_OPINION].includes(agenda!);
      },
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.ES_FILES}/${id}/regulation/list`);
        source.setParams({
          size: -1,
          sort: [
            {
              field: 'label',
              order: 'ASC',
              type: 'FIELD',
            },
          ],
        });
      },
    },
  };
}

function useRegulationTypeSource(
  sourceKey: string,
  regulationType: RegulationType
): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [sourceKey]: {
      source,
      shouldLoad: ({ agenda }) => {
        return [Agenda.COURT, Agenda.PLENARY_OPINION].includes(agenda!);
      },
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.ES_FILES}/${id}/regulation/list`);
        source.setParams({
          size: -1,
          sort: [
            {
              field: 'valueSort',
              order: 'ASC',
              type: 'FIELD',
            },
          ],
          filters: [
            {
              field: 'regulationType.id',
              operation: ApiFilterOperation.EQ,
              value: regulationType,
            },
          ],
        });
      },
    },
  };
}

function useChallengedActSource(): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [CHALLENGED_ACT_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => {
        return [Agenda.COURT].includes(agenda!);
      },
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.ES_FILES}/${id}/challenged-act/list`);
        source.setParams({
          sort: [
            {
              field: 'actType.name',
              order: 'ASC',
              type: 'FIELD',
            },
          ],
        });
      },
    },
  };
}

function useTaskSource(): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [TASK_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => {
        return [
          Agenda.COURT,
          Agenda.INF,
          Agenda.SPR,
          Agenda.PLENARY_OPINION,
          Agenda.DISCIPLINARY_PROCEEDINGS,
        ].includes(agenda!);
      },
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.TASKS}/list`);
        source.setParams({
          size: 15,
          sort: [
            {
              field: 'created',
              type: 'FIELD',
              order: 'DESC',
            },
          ],
          filters: [
            {
              field: 'esFile.id',
              operation: ApiFilterOperation.EQ,
              value: id,
            },
            {
              field: 'state.id',
              operation: ApiFilterOperation.EQ,
              value: TaskState.SOLVING,
            },
          ],
        });
      },
    },
  };
}

function useDraftSource(): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [DRAFT_SOURCE]: {
      source,
      shouldLoad: () => false,
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.ES_FILES}/${id}/draft/list`);
      },
    },
  };
}

function useViewingSource(): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [VIEWING_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => {
        return [Agenda.DISCIPLINARY_PROCEEDINGS].includes(agenda!);
      },
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.ES_FILES}/${id}/viewing/list`);
      },
    },
  };
}

function useViewingRequestSource(): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [VIEWING_REQUEST_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => {
        return [Agenda.COURT].includes(agenda!);
      },
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.ES_FILES}/${id}/viewing/request/list`);
        source.setParams({
          size: 10,
          sort: [
            {
              field: 'created',
              type: 'FIELD',
              order: 'DESC',
            },
          ],
        });
      },
    },
  };
}

function useLoanSource(): HookedSource<EsFile> {
  const source = useScrollableSource();

  return {
    [LOAN_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => [Agenda.COURT].includes(agenda!),
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.LOAN}/list`);
        source.setParams({
          size: 10,
          sort: [{ field: 'loanedOn', type: 'FIELD', order: 'DESC' }],
          filters: [
            {
              field: 'loanedFile.id',
              operation: ApiFilterOperation.EQ,
              value: id,
            },
          ],
        });
      },
    },
  };
}

function useSameProposerFileSource(): HookedSource<EsFile> {
  const source = useScrollableSource({
    url: `${EvidenceApiUrl.ES_FILES}/list`,
  });

  const targetParticipantTypes = [
    ParticipantType.PROPOSER,
    ParticipantType.WRITER,
    ParticipantType.ACCESSORY_PARTICIPANT,
    ParticipantType.LEGAL_ADVISER,
  ];

  return {
    [SAME_PROPOSER_FILE_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => {
        return [Agenda.COURT, Agenda.INF, Agenda.SPR].includes(agenda!);
      },
      modifyBeforeLoad: ({ participants, id }) => {
        const proposerSubjectIds = (participants ?? [])
          .filter(
            (p) =>
              p.type === ParticipantType.PROPOSER ||
              p.type === ParticipantType.WRITER
          )
          .map((p) => p.contact?.subject?.id)
          .filter(Boolean);

        source.setParams({
          size: 10,
          filters: [
            {
              field: 'agenda.id',
              operation: ApiFilterOperation.IN,
              values: [Agenda.COURT, Agenda.SPR, Agenda.INF],
            },
            {
              operation: ApiFilterOperation.NOT,
              filters: [
                {
                  field: 'id',
                  operation: ApiFilterOperation.EQ,
                  value: id,
                },
              ],
            },
            {
              operation: ApiFilterOperation.NESTED,
              path: 'participants',
              filter: {
                operation: ApiFilterOperation.AND,
                filters: [
                  {
                    field: 'participants.contact.subject.id',
                    operation: ApiFilterOperation.IN,
                    values: proposerSubjectIds,
                  },
                  {
                    field: 'participants.type.id',
                    operation: ApiFilterOperation.IN,
                    values: targetParticipantTypes,
                  },
                ],
              },
            },
          ],
        });
      },
    },
  };
}

function useSameLegalAdviserFileSource(): HookedSource<EsFile> {
  const source = useScrollableSource({
    url: `${EvidenceApiUrl.ES_FILES}/list`,
  });

  const targetAgendas = [Agenda.COURT, Agenda.SPR, Agenda.INF];
  const targetParticipantTypes = [
    ParticipantType.PROPOSER,
    ParticipantType.WRITER,
    ParticipantType.ACCESSORY_PARTICIPANT,
    ParticipantType.LEGAL_ADVISER,
  ];

  return {
    [SAME_LEGAL_ADVISER_FILE_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => {
        return targetAgendas.includes(agenda!);
      },
      modifyBeforeLoad: ({ participants, id }) => {
        const legalAdviserSubjectIds = (participants ?? [])
          .filter((p) => p.type === ParticipantType.LEGAL_ADVISER)
          .map((p) => p.contact?.subject?.id)
          .filter(Boolean);

        source.setParams({
          size: 10,
          filters: [
            {
              field: 'agenda.id',
              operation: ApiFilterOperation.IN,
              values: targetAgendas,
            },
            {
              operation: ApiFilterOperation.NOT,
              filters: [
                {
                  field: 'id',
                  operation: ApiFilterOperation.EQ,
                  value: id,
                },
              ],
            },
            {
              operation: ApiFilterOperation.NESTED,
              path: 'participants',
              filter: {
                operation: ApiFilterOperation.AND,
                filters: [
                  {
                    field: 'participants.contact.subject.id',
                    operation: ApiFilterOperation.IN,
                    values: legalAdviserSubjectIds,
                  },
                  {
                    field: 'participants.type.id',
                    operation: ApiFilterOperation.IN,
                    values: targetParticipantTypes,
                  },
                ],
              },
            },
          ],
        });
      },
    },
  };
}

function useSameChallengedActFileSource(): HookedSource<CourtFile> {
  const source = useScrollableSource({
    url: `${EvidenceApiUrl.ES_FILES}/list`,
  });

  return {
    [SAME_CHALLENGED_ACT_FILE_SOURCE]: {
      source,
      shouldLoad: ({ agenda }) => {
        return [Agenda.COURT].includes(agenda!);
      },
      modifyBeforeLoad: ({ challengedActs, id }) => {
        const challengedActsWithExternalFileNumber = (
          challengedActs ?? []
        ).filter((c) => !!c.externalFileNumber);

        source.setParams({
          size: 10,
          filters: [
            {
              field: 'agenda.id',
              operation: ApiFilterOperation.EQ,
              value: Agenda.COURT,
            },
            {
              operation: ApiFilterOperation.NOT,
              filters: [
                {
                  field: 'id',
                  operation: ApiFilterOperation.EQ,
                  value: id,
                },
              ],
            },
            {
              field: 'challengedActs.externalFileNumber',
              operation: ApiFilterOperation.IN,
              values: challengedActsWithExternalFileNumber.map(
                (c: ChallengedAct) => c.externalFileNumber
              ),
            },
          ],
        });
      },
    },
  };
}

export function useEsFilesHookedSources(): HookedSource<EsFile> {
  const notes = useNoteSource();
  const deadlines = useDeadlineSource();
  const runningDeadlines = useRunningDeadlineSource();
  const documents = useDocumentSource();
  const coverLetters = useCoverLetterSource();
  const decisions = useDecisionSource();
  const proposers = useProposerSource();
  const accessoryProposers = useAccessoryProposerSource();
  const legalAdvisers = useLegalAdviserSource();
  const writers = useWriterSource();
  const challengedActs = useChallengedActSource();
  const regulations = useRegulationSource();
  const constitutionalRegulations = useRegulationTypeSource(
    REGULATION_CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES_SOURCE,
    RegulationType.CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES
  );
  const europeanUnionRegulations = useRegulationTypeSource(
    REGULATION_EUROPEAN_UNION_LAW_SOURCE,
    RegulationType.EUROPEAN_UNION_LAW
  );
  const otherRegulations = useRegulationTypeSource(
    REGULATION_OTHER_SOURCE,
    RegulationType.OTHER
  );
  const tasks = useTaskSource();
  const drafts = useDraftSource();
  const viewing = useViewingSource();
  const viewingRequests = useViewingRequestSource();
  const loans = useLoanSource();
  const sameProposerFiles = useSameProposerFileSource();
  const sameLegalAdviserFiles = useSameLegalAdviserFileSource();
  const sameChallengedActFiles = useSameChallengedActFileSource();
  const history = useHistorySource({
    entityType: HistoryEntityType.ES_FILE,
  });

  return {
    ...notes,
    ...deadlines,
    ...runningDeadlines,
    ...documents,
    ...coverLetters,
    ...decisions,
    ...proposers,
    ...accessoryProposers,
    ...legalAdvisers,
    ...writers,
    ...challengedActs,
    ...regulations,
    ...constitutionalRegulations,
    ...europeanUnionRegulations,
    ...otherRegulations,
    ...tasks,
    ...drafts,
    ...viewing,
    ...viewingRequests,
    ...loans,
    ...sameProposerFiles,
    ...sameLegalAdviserFiles,
    ...sameChallengedActFiles,
    ...history,
  };
}
